import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { WarehousesService } from '../../../../services/warehouses.service';
import { StatusService } from '../../../../services/status.service';
import { Status } from '../../../../models/Status';
import { StaffService } from '../../../../services/staff.service';
import { StaffMember } from '../../../../models/StaffMember';
import { ViaCepService, ViaCepResponse } from '../../../../services/viacep.service';


@Component({
  selector: 'app-warehouse-form',
  templateUrl: './warehouse-form.component.html',
  styleUrls: ['./warehouse-form.component.css']
})
export class WarehouseFormComponent implements OnInit {
  formStep1: FormGroup;
  formStep2: FormGroup;
  formStep3: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  availableTabs = [
    { id: 'step1', label: 'Identificação', icon: 'fas fa-info-circle', required: true, completed: false },
    { id: 'step2', label: 'Localização e Responsável', icon: 'fas fa-map-marker-alt', required: false, completed: false },
    { id: 'step3', label: 'Capacidade e Configurações', icon: 'fas fa-chart-area', required: false, completed: false }
  ];
  activeTab = 'step1';
  warehouseId?: number;
  isSubmitting = false;

  // Opções para selects
  warehouseTypes = [
    'Principal (Sede)',
    'Filial',
    'Oficina',
    'Showroom',
    'Depósito de Peças',
    'Almoxarifado de Segurança'
  ];

  warehouseCategories = [
    'Peças de Motos',
    'Acessórios de Segurança', 
    'Ferramentas',
    'Produtos de Limpeza',
    'Documentação',
    'Equipamentos de Escritório',
    'Suprimentos Gerais'
  ];

  statusOptions: Status[] = [];
  staffMembers: StaffMember[] = [];
  loadingStaff = false;
  isLoadingCep = false;
  cepError: string | null = null;

  departments = [
    'Oficina',
    'Vendas',
    'Locação',
    'Administrativo',
    'Segurança',
    'Limpeza',
    'TI'
  ];

  branches = [
    'Matriz',
    'Filial Norte',
    'Filial Sul',
    'Filial Leste',
    'Filial Oeste'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private warehousesService: WarehousesService,
    private location: Location,
    private statusService: StatusService,
    private staffService: StaffService,
    private viaCep: ViaCepService
  ) {
    this.formStep1 = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      type: ['Principal (Sede)', Validators.required],
      category: [''],
      status: ['', Validators.required]
    });

    this.formStep2 = this.fb.group({
      address: ['', Validators.maxLength(200)],
      city: ['', Validators.maxLength(100)],
      state: ['', Validators.maxLength(10)],
      zipCode: ['', Validators.maxLength(10)],
      country: ['', Validators.maxLength(100)],
      building: ['', Validators.maxLength(100)],
      floor: ['', Validators.maxLength(50)],
      room: ['', Validators.maxLength(50)],
      department: [''],
      branch: [''],
      managerStaffId: [null, Validators.required],
      responsiblePerson: ['', Validators.maxLength(200)],
      responsibleEmail: ['', [Validators.email, Validators.maxLength(100)]],
      responsiblePhone: ['', Validators.maxLength(20)]
    });

    this.formStep3 = this.fb.group({
      totalArea: [null, [Validators.min(0)]],
      usedArea: [null, [Validators.min(0)]],
      availableArea: [null, [Validators.min(0)]],
      maxCapacity: [null, [Validators.min(0)]],
      currentCapacity: [null, [Validators.min(0)]],
      maxWeight: [null, [Validators.min(0)]],
      currentWeight: [null, [Validators.min(0)]],
      startDate: [''],
      endDate: [''],
      isActive: [true],
      isMainWarehouse: [false],
      requiresAuthorization: [false],
      hasSecurity: [false],
      hasTemperatureControl: [false],
      hasHumidityControl: [false],
      notes: ['', Validators.maxLength(1000)]
    });
  }

  ngOnInit(): void {
    this.loadStatuses();
    this.loadStaffMembers();
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.warehouseId = Number(idParam);
      this.loadWarehouse();
    }
    this.formStep2.get('managerStaffId')?.valueChanges.subscribe((rawId: number | string | null) => {
      if (rawId === null || rawId === undefined || rawId === '') {
        return;
      }

      const staffId = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
      if (!staffId || isNaN(staffId)) {
        return;
      }

      const staff = this.staffMembers.find(s => s.Id === staffId);
      if (staff) {
        this.formStep2.patchValue({
          responsiblePerson: staff.FullName || this.formStep2.value.responsiblePerson,
          // Email permanece manual; não vem de Staff
          responsiblePhone: staff.CompanyPhone || this.formStep2.value.responsiblePhone
        }, { emitEvent: false });
      }
    });
    this.syncTabFromStep();
    this.updateTabsCompletionStatus();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('Warehouse').subscribe({
      next: (statuses) => {
        this.statusOptions = statuses;
      },
      error: (err) => {
        console.error('Erro ao carregar status', err);
        this.statusOptions = [];
      }
    });
  }

  loadStaffMembers(): void {
    this.loadingStaff = true;
    this.staffService.getStaffPaginated({ page: 1, pageSize: 500 }).subscribe({
      next: (response) => {
        this.staffMembers = response?.staff || [];
        this.loadingStaff = false;
      },
      error: () => {
        this.staffMembers = [];
        this.loadingStaff = false;
      }
    });
  }

  onResponsiblePhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskPhone(input.value || '');
    this.formStep2.get('responsiblePhone')?.setValue(masked, { emitEvent: false });
  }

  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCEP(input.value || '');
    this.formStep2.get('zipCode')?.setValue(masked, { emitEvent: false });

    const digits = this.onlyDigits(masked);
    if (digits.length === 8) {
      this.fetchAddressByCEP(digits);
    } else {
      this.cepError = null;
    }
  }

  private fetchAddressByCEP(cepDigits: string): void {
    if (!cepDigits || cepDigits.length !== 8) return;
    this.isLoadingCep = true;
    this.cepError = null;
    this.viaCep.lookup(cepDigits).subscribe({
      next: (res: ViaCepResponse) => {
        this.isLoadingCep = false;
        if ((res as any)?.erro) {
          this.cepError = 'CEP não encontrado.';
          return;
        }
        this.formStep2.patchValue({
          address: res.logradouro || this.formStep2.get('address')?.value,
          city: res.localidade || this.formStep2.get('city')?.value,
          state: res.uf || this.formStep2.get('state')?.value,
          // Mantém country manual; complementos podem ser tratados depois se desejar
        }, { emitEvent: false });
      },
      error: () => {
        this.isLoadingCep = false;
        this.cepError = 'Erro ao consultar o CEP.';
      }
    });
  }

  private maskPhone(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 11);
    if (digits.length <= 10) {
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }

  private maskCEP(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 8);
    return digits.replace(/(\d{5})(\d)/, '$1-$2');
  }

  private onlyDigits(value: string | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  loadWarehouse(): void {
    if (!this.warehouseId) return;
    
    this.warehousesService.getById(this.warehouseId).subscribe({
      next: warehouse => {
        this.formStep1.patchValue({
          code: warehouse.code,
          name: warehouse.name,
          description: warehouse.description,
          type: warehouse.type,
          category: warehouse.category,
          status: warehouse.status
        });

        this.formStep2.patchValue({
          address: warehouse.address,
          city: warehouse.city,
          state: warehouse.state,
          zipCode: warehouse.zipCode,
          country: warehouse.country,
          building: warehouse.building,
          floor: warehouse.floor,
          room: warehouse.room,
          department: warehouse.department,
          branch: warehouse.branch,
          managerStaffId: warehouse.managerStaffId || null,
          responsiblePerson: warehouse.responsiblePerson,
          responsibleEmail: warehouse.responsibleEmail,
          responsiblePhone: warehouse.responsiblePhone
        });

        this.formStep3.patchValue({
          totalArea: warehouse.totalArea,
          usedArea: warehouse.usedArea,
          availableArea: warehouse.availableArea,
          maxCapacity: warehouse.maxCapacity,
          currentCapacity: warehouse.currentCapacity,
          maxWeight: warehouse.maxWeight,
          currentWeight: warehouse.currentWeight,
          startDate: warehouse.startDate ? warehouse.startDate.substring(0, 10) : '',
          endDate: warehouse.endDate ? warehouse.endDate.substring(0, 10) : '',
          isActive: warehouse.isActive,
          isMainWarehouse: warehouse.isMainWarehouse,
          requiresAuthorization: warehouse.requiresAuthorization,
          hasSecurity: warehouse.hasSecurity,
          hasTemperatureControl: warehouse.hasTemperatureControl,
          hasHumidityControl: warehouse.hasHumidityControl,
          notes: warehouse.notes
        });
      },
      error: _ => {
        this.router.navigate(['/stock/warehouses']);
      }
    });
  }

  nextStep(): void { 
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    }
  }
  
  prevStep(): void { 
    if (this.currentStep > 1) {
      this.currentStep--;
      this.syncTabFromStep();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.syncStepFromTab();
  }

  save(): void {
    const isStep1Valid = this.formStep1.valid;
    const isStep2Valid = this.formStep2.valid;
    const isStep3Valid = this.formStep3.valid;
    
    if (!isStep1Valid || !isStep2Valid || !isStep3Valid) {
      this.markFormGroupTouched();
      return;
    }

    this.isSubmitting = true;
    const payload = { 
      ...this.formStep1.value, 
      ...this.formStep2.value, 
      ...this.formStep3.value 
    } as any;

    // Converter datas para ISO string
    if (payload.startDate) {
      payload.startDate = new Date(payload.startDate).toISOString();
    }
    if (payload.endDate) {
      payload.endDate = new Date(payload.endDate).toISOString();
    }

    if (this.warehouseId) {
      this.warehousesService.update(this.warehouseId, payload).subscribe({
        next: _ => {
          this.isSubmitting = false;
          this.router.navigate(['/stock/warehouses']);
        },
        error: _ => {
          this.isSubmitting = false;
        }
      });
    } else {
      this.warehousesService.create(payload).subscribe({
        next: _ => {
          this.isSubmitting = false;
          this.router.navigate(['/stock/warehouses']);
        },
        error: _ => {
          this.isSubmitting = false;
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/stock/warehouses']);
  }

  goBack(): void {
    try {
      if (window.history.length > 1) {
        this.location.back();
        return;
      }
    } catch (_) { /* noop */ }
    this.router.navigate(['/stock/warehouses']);
  }

  markFormGroupTouched(): void {
    Object.keys(this.formStep1.controls).forEach(key => {
      const control = this.formStep1.get(key);
      control?.markAsTouched();
    });
    Object.keys(this.formStep2.controls).forEach(key => {
      const control = this.formStep2.get(key);
      control?.markAsTouched();
    });
    Object.keys(this.formStep3.controls).forEach(key => {
      const control = this.formStep3.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const formGroup = this.getFormGroupForField(fieldName);
    const field = formGroup?.get(fieldName);
    return !!(field?.invalid && field?.touched);
  }

  getFieldError(fieldName: string): string {
    const formGroup = this.getFormGroupForField(fieldName);
    const field = formGroup?.get(fieldName);
    
    if (!field?.errors) return '';
    
    if (field.errors['required']) return 'Campo obrigatório';
    if (field.errors['minlength']) return `Mínimo de ${field.errors['minlength'].requiredLength} caracteres`;
    if (field.errors['maxlength']) return `Máximo de ${field.errors['maxlength'].requiredLength} caracteres`;
    if (field.errors['email']) return 'Email inválido';
    if (field.errors['min']) return `Valor mínimo é ${field.errors['min'].min}`;
    
    return 'Campo inválido';
  }

  private getFormGroupForField(fieldName: string): FormGroup | null {
    if (this.formStep1.contains(fieldName)) return this.formStep1;
    if (this.formStep2.contains(fieldName)) return this.formStep2;
    if (this.formStep3.contains(fieldName)) return this.formStep3;
    return null;
  }

  // Getters para facilitar o acesso no template
  get code() { return this.formStep1.get('code'); }
  get name() { return this.formStep1.get('name'); }
  get description() { return this.formStep1.get('description'); }
  get type() { return this.formStep1.get('type'); }
  get category() { return this.formStep1.get('category'); }
  get status() { return this.formStep1.get('status'); }
  
  get address() { return this.formStep2.get('address'); }
  get city() { return this.formStep2.get('city'); }
  get state() { return this.formStep2.get('state'); }
  get zipCode() { return this.formStep2.get('zipCode'); }
  get country() { return this.formStep2.get('country'); }
  get building() { return this.formStep2.get('building'); }
  get floor() { return this.formStep2.get('floor'); }
  get room() { return this.formStep2.get('room'); }
  get department() { return this.formStep2.get('department'); }
  get branch() { return this.formStep2.get('branch'); }
  get managerStaffId() { return this.formStep2.get('managerStaffId'); }
  get responsiblePerson() { return this.formStep2.get('responsiblePerson'); }
  get responsibleEmail() { return this.formStep2.get('responsibleEmail'); }
  get responsiblePhone() { return this.formStep2.get('responsiblePhone'); }
  
  get totalArea() { return this.formStep3.get('totalArea'); }
  get usedArea() { return this.formStep3.get('usedArea'); }
  get availableArea() { return this.formStep3.get('availableArea'); }
  get maxCapacity() { return this.formStep3.get('maxCapacity'); }
  get currentCapacity() { return this.formStep3.get('currentCapacity'); }
  get maxWeight() { return this.formStep3.get('maxWeight'); }
  get currentWeight() { return this.formStep3.get('currentWeight'); }
  get notes() { return this.formStep3.get('notes'); }

  private syncTabFromStep(): void {
    this.activeTab = `step${this.currentStep}`;
  }

  private syncStepFromTab(): void {
    const parsed = Number(this.activeTab.replace('step', ''));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= this.totalSteps) {
      this.currentStep = parsed;
    }
  }

  private updateTabsCompletionStatus(): void {
    this.availableTabs.forEach((tab, index) => {
      tab.completed = this.currentStep > index + 1;
    });
  }
}
